import React, { useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarIcon, MapPin, Users, Clock } from 'lucide-react';

const localizer = momentLocalizer(moment);

const EventsCalendar = ({ events, onSelectEvent, onRSVP }) => {
  // Transform events for the calendar
  const calendarEvents = useMemo(() => {
    return events.map(event => {
      // Combine date and time for start
      const startDateTime = moment(`${event.date} ${event.time}`, 'YYYY-MM-DD HH:mm:ss').toDate();
      // Assume 2 hour duration if not specified
      const endDateTime = moment(startDateTime).add(2, 'hours').toDate();
      
      return {
        id: event.id,
        title: event.title,
        start: startDateTime,
        end: endDateTime,
        resource: event
      };
    });
  }, [events]);

  // Custom event style
  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: '#e11d48',
      borderRadius: '4px',
      opacity: 0.9,
      color: 'white',
      border: 'none',
      display: 'block',
      fontSize: '0.85rem',
      padding: '2px 5px'
    };
    return { style };
  };

  // Custom event component for calendar cells
  const EventComponent = ({ event }) => (
    <span className="flex items-center gap-1">
      <CalendarIcon className="w-3 h-3" />
      <span className="truncate">{event.title}</span>
    </span>
  );

  return (
    <div className="events-calendar-wrapper">
      <style>{`
        .rbc-calendar {
          font-family: inherit;
          min-height: 600px;
        }
        .rbc-header {
          padding: 12px 3px;
          font-weight: 600;
          color: #881337;
          border-bottom: 2px solid #fecdd3;
          background: #fff1f2;
        }
        .rbc-today {
          background-color: #ffe4e6;
        }
        .rbc-event {
          cursor: pointer;
          transition: all 0.2s;
        }
        .rbc-event:hover {
          transform: scale(1.02);
          box-shadow: 0 2px 8px rgba(225, 29, 72, 0.3);
        }
        .rbc-month-view {
          border: 1px solid #fecdd3;
          border-radius: 8px;
          overflow: hidden;
        }
        .rbc-date-cell {
          padding: 8px;
        }
        .rbc-off-range-bg {
          background: #fafafa;
        }
        .rbc-toolbar {
          padding: 15px;
          margin-bottom: 15px;
          background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
          border-radius: 8px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .rbc-toolbar button {
          padding: 8px 16px;
          border-radius: 6px;
          border: 1px solid #fda4af;
          background: white;
          color: #881337;
          font-weight: 500;
          transition: all 0.2s;
        }
        .rbc-toolbar button:hover {
          background: #e11d48;
          color: white;
          border-color: #e11d48;
        }
        .rbc-toolbar button.rbc-active {
          background: #e11d48;
          color: white;
          border-color: #e11d48;
        }
      `}</style>
      
      <Calendar
        localizer={localizer}
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 600 }}
        onSelectEvent={onSelectEvent}
        eventPropGetter={eventStyleGetter}
        components={{
          event: EventComponent
        }}
        views={['month', 'week', 'day', 'agenda']}
        defaultView="month"
      />
    </div>
  );
};

export default EventsCalendar;
